import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

const NODES = [
  { icon: '▣', label: 'מסך', sub: 'הנקודה הפיזית', color: '#9a7209' },
  { icon: '◷', label: 'מערך תצוגה', sub: 'לוח זמנים שבועי', color: '#2c4a6e' },
  { icon: '◫', label: 'לוח', sub: 'בד עיצוב עצמאי', color: '#9a7209' },
  { icon: '⊞', label: 'רכיבים', sub: 'נתונים חיים', color: '#9a7209' },
];

const EXPLANATIONS = [
  {
    title: 'המסך — הנקודה שהקהל רואה',
    body: 'כל מסך פיזי — צג, טאבלט, או כל מסך HDMI — הופך לנקודת תצוגה חכמה. אין תוכנות להתקין ואין חומרה מיוחדת לקנות. פותחים דפדפן, מכניסים כתובת URL שinorai מייצר — והמסך מחובר ומתחיל לשדר מיד.',
    value: 'כל מסך שכבר יש לך — עובד. inorai לא מחייב ציוד חדש.',
    example: 'הצג שתלוי בכניסה לבית הכנסת? שתי דקות הגדרה והוא הופך לתצוגה חיה שמנהלת את עצמה.',
  },
  {
    title: 'מערך תצוגה — מה המסך מציג, ומתי',
    body: 'נתחיל מהפשוט: מסך אחד, לוח אחד — תמיד דולק. עבור הרבה בתי כנסת זה כל מה שצריך.\n\nאבל מערך תצוגה יכול להכיל יותר מלוח אחד. כשמוסיפים כמה לוחות, הם מתחלפים על המסך בסבב כל כמה שניות — לוח זמנים, אחריו לוח שמחות, אחריו לוח אירוע — וחוזר חלילה. כך מציגים יותר תוכן בלי לדחוס הכל לעיצוב אחד.\n\nומעבר לזה — ניתן ליצור מערכי תצוגה שונים לחלוטין לזמנים שונים. מערך לחול, מערך לשבת, מערך לחגים. המסך עובר ביניהם אוטומטית לפי לוח הזמנים שהגדרת.',
    value: 'לא צריך לדחוס הכל לעיצוב אחד. יש הכרזה חשובה שרוצים שתבלוט, אירוע שרוצים לפמפם, תרומה שמגייסים? מוסיפים לוח לרוטציה — הוא מקבל חשיפה בלי שיפגע בתוכן הקבוע.',
    example: 'בר מצווה בשבת, שיעור ביום חמישי, גיוס כספים לשיפוץ — כל אחד מקבל לוח משלו, מוצג לסירוגין בין הזמנים הרגילים. הקהל רואה הכל.',
  },
  {
    title: 'לוח — מה שהקהל רואה ברגע נתון',
    body: 'לוח הוא עיצוב מסך אחד — בד חופשי שבו אתה מסדר רכיבים בדיוק כפי שרצית. כל לוח עצמאי לחלוטין: לוח שבת נראה אחרת מלוח חול, ולוח חגים — אחרת משניהם.\n\nלכל לוח נשמרות 5 גרסאות אחרונות — אם שיניתי משהו ולא אהבתי, לוחץ אחורה ומחזיר בדיוק מה שהיה.',
    value: 'גמישות של עיצוב חופשי, בפשטות של גרירת עכבר. ובונים לוח חדש מבלי לגעת בלוחות הקיימים.',
    example: 'לוח שחרית מציג זמני תפילה בגדול. לוח שבת — פרשה, דרשה, שעות כניסה ויציאה. כל אחד מעוצב לפי הצורך.',
  },
  {
    title: 'רכיבים — הנתונים החיים שבתוך הלוח',
    body: 'רכיבים הם הבניינים שמרכיבים כל לוח. כל רכיב מציג סוג מידע אחד — ומתעדכן לבד, ללא כל התערבות ידנית. זמני תפילה מחושבים לפי מיקומך ולפי ההלכה, לוח עברי, שמחות קהילה, מזג אוויר, מודעות, פרנסים ועוד.\n\nרכיב שהגדרת פעם אחת אפשר לשמור ולהשתמש בו שוב — בלוח אחר, בפעם הבאה, בלי להתחיל מאפס.',
    value: '21+ סוגי רכיבים, 105+ תבניות עיצוב. כל רכיב מביא נתונים חיים — אתה רק בוחר מה להציג ואיפה.',
    example: 'רכיב "זמני תפילה" יודע היכן בית הכנסת שלך, ומחשב כל יום מחדש: נץ, שקיעה, מנחה, ערבית — בדיוק לאותו יום.',
  },
];


const ArchitectureSection = () => {
  const sectionRef  = useRef(null);
  const [visible, setVisible]     = useState(false);
  const [activeNode, setActiveNode] = useState(0);
  const [paused, setPaused]       = useState(false);  // manual click stops auto-cycle

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* Auto-cycle — stops permanently once the user clicks a node */
  useEffect(() => {
    if (!visible || paused) return;
    const id = setInterval(() => setActiveNode(p => (p + 1) % NODES.length), 3500);
    return () => clearInterval(id);
  }, [visible, paused]);

  const handleNodeClick = (i) => {
    setActiveNode(i);
    setPaused(true);
  };

  return (
    <Box
      id="features"
      ref={sectionRef}
      sx={{
        bgcolor: '#152a47',
        py: { xs: 10, md: 14 },
        px: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Base dot grid — static, matches Hero */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(218,165,32,0.06) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />
      {/* Gold sparkle group A */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(218,165,32,0.22) 1.5px, transparent 1.5px)',
        backgroundSize: '112px 112px', backgroundPosition: '0 0',
        animation: 'heroDotTwinkle 2.9s ease-in-out infinite',
      }} />
      {/* Gold sparkle group B */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(200,155,20,0.16) 1.5px, transparent 1.5px)',
        backgroundSize: '84px 84px', backgroundPosition: '14px 28px',
        animation: 'heroDotTwinkle 3.7s ease-in-out 1.3s infinite',
      }} />
      {/* Gold sparkle group C */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(255,210,60,0.2) 1.5px, transparent 1.5px)',
        backgroundSize: '140px 112px', backgroundPosition: '42px 14px',
        animation: 'heroDotTwinkle 4.4s ease-in-out 2.5s infinite',
      }} />
      {/* Gold sparkle group D */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(218,165,32,0.14) 1px, transparent 1px)',
        backgroundSize: '196px 140px', backgroundPosition: '70px 56px',
        animation: 'heroDotTwinkle 5.1s ease-in-out 0.7s infinite',
      }} />
      {/* Radial gold wash — top left */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, width: '50vw', height: '65vh',
        background: 'radial-gradient(ellipse at top left, rgba(154,114,9,0.15) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />
      {/* Large orbital circle with comet — CW */}
      <Box sx={{
        position: 'absolute', left: '-18%', top: '50%',
        transform: 'translateY(-50%)', width: '68vw', height: '68vw',
        borderRadius: '50%', border: '1px solid rgba(154,114,9,0.18)', pointerEvents: 'none',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'conic-gradient(from 0deg, transparent 93%, rgba(154,114,9,0.08) 95%, rgba(218,165,32,0.5) 96.5%, rgba(255,230,80,0.9) 97.5%, rgba(218,165,32,0.5) 98.5%, rgba(154,114,9,0.08) 99.5%, transparent 100%)',
          WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          mask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          animation: 'spinCCW 26s linear infinite',
        }} />
      </Box>
      {/* Medium orbital circle — CCW */}
      <Box sx={{
        position: 'absolute', right: '-10%', top: '50%',
        transform: 'translateY(-50%)', width: '44vw', height: '44vw',
        borderRadius: '50%', border: '1px solid rgba(154,114,9,0.13)', pointerEvents: 'none',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'conic-gradient(from 180deg, transparent 93%, rgba(154,114,9,0.06) 95%, rgba(218,165,32,0.4) 96.5%, rgba(255,230,80,0.75) 97.5%, rgba(218,165,32,0.4) 98.5%, rgba(154,114,9,0.06) 99.5%, transparent 100%)',
          WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          mask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          animation: 'spinCW 38s linear infinite',
        }} />
      </Box>

      <Box sx={{ maxWidth: '1000px', mx: 'auto', position: 'relative' }}>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
          <Typography sx={{
            fontFamily: 'Secular One, sans-serif', color: '#9a7209',
            fontSize: '0.85rem', letterSpacing: '0.12em',
            textTransform: 'uppercase', mb: 2,
          }}>
            איך זה עובד
          </Typography>
          <Typography component="h2" dir="rtl" sx={{
            fontFamily: 'Frank Ruhl Libre, serif', color: 'white',
            fontSize: { xs: '1.9rem', md: '2.5rem' },
            fontWeight: 900, letterSpacing: '-0.02em', mb: 2,
            direction: 'rtl',
          }}>
            מבנה פשוט.{' '}
            <span className="lp-gold-text">שליטה</span>
            {' '}מלאה.
          </Typography>
          <Typography sx={{
            color: 'rgba(255,255,255,0.72)', fontFamily: 'Heebo, sans-serif',
            fontSize: '1rem', maxWidth: '480px', mx: 'auto', lineHeight: 1.8,
          }}>
            מסך, מערך, לוח, רכיב — ארבעה חלקים שנבנים זה על זה ונותנים שליטה מלאה ופשוטה. לחץ על כל אחד כדי להכיר אותו.
          </Typography>
        </Box>

        {/* Nodes row */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          mb: 6,
        }}>
          {NODES.map((node, i) => {
            const isActive = activeNode === i;
            return (
              <Box
                key={i}
                sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}
              >
                {/*
                 * Border trick (correct):
                 *   Outer wrapper = overflow:hidden + borderRadius → clips children to rounded rect.
                 *   Spinning element = large square centered, rotates → conic-gradient sweeps around.
                 *   Inner card = m:'2px' creates the 2px gap = the visible "border" strip.
                 */}
                <Box
                  onClick={() => handleNodeClick(i)}
                  sx={{
                    position: 'relative',
                    borderRadius: '14px',
                    overflow: 'hidden',          // ← clips spinning gradient to card shape
                    cursor: 'pointer',
                    boxShadow: isActive ? 'none' : 'inset 0 0 0 1.5px rgba(255,255,255,0.09)',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(16px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease',
                    transitionDelay: `${i * 0.25}s`,
                  }}
                >
                  {/* Spinning border:
                      - Outer box handles translate (stays still)
                      - Inner box handles rotate only (no translate conflict)
                      - Gradient is always gold (dim→bright→dim) so border is visible all the way round */}
                  {isActive && (
                    <Box sx={{
                      position: 'absolute',
                      width: '320px', height: '320px',
                      top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 0,
                    }}>
                      <Box sx={{
                        width: '100%', height: '100%',
                        background: 'conic-gradient(from 0deg, rgba(154,114,9,0.35) 0deg, rgba(212,160,23,0.7) 60deg, #d4a017 90deg, rgba(212,160,23,0.7) 120deg, rgba(154,114,9,0.35) 200deg, rgba(154,114,9,0.18) 280deg, rgba(154,114,9,0.35) 360deg)',
                        animation: 'spinCW 3s linear infinite',
                      }} />
                    </Box>
                  )}

                  {/* Card — opaque + m:2px reveals the 2px border strip */}
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    p: 2.5, m: '2px', borderRadius: '12px',
                    bgcolor: isActive ? '#1d3a62' : '#152a47',
                    transition: 'background-color 0.35s ease',
                    minWidth: { xs: '100px', md: '120px' },
                    position: 'relative', zIndex: 1,
                    '&:hover': { bgcolor: isActive ? '#1d3a62' : '#1b3257' },
                  }}>
                    <Typography sx={{
                      fontSize: '1.4rem', mb: 1,
                      color: node.color === '#9a7209' ? '#d4a017' : 'rgba(255,255,255,0.6)',
                      fontFamily: 'Rubik, sans-serif',
                      animation: isActive ? 'heroHeartbeat 2.8s ease-in-out infinite' : 'none',
                      display: 'block',
                    }}>
                      {node.icon}
                    </Typography>
                    <Typography sx={{
                      color: 'white', fontFamily: 'Secular One, sans-serif',
                      fontSize: '0.9rem', textAlign: 'center', mb: 0.25,
                    }}>
                      {node.label}
                    </Typography>
                    <Typography sx={{
                      color: node.color === '#9a7209' ? '#9a7209' : 'rgba(255,255,255,0.65)',
                      fontFamily: 'Heebo, sans-serif', fontSize: '0.7rem', textAlign: 'center',
                    }}>
                      {node.sub}
                    </Typography>
                  </Box>
                </Box>

                {/* Connector arrow */}
                {i < NODES.length - 1 && (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: { md: 0.5 }, my: { xs: 0.5, md: 0 },
                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                    transform: { xs: 'rotate(90deg)', md: 'none' },
                    color: isActive ? 'rgba(154,114,9,0.8)' : 'rgba(255,255,255,0.2)',
                    transition: 'color 0.5s ease',
                    opacity: visible ? 1 : 0,
                    transitionDelay: `${(i + 0.5) * 0.25}s`,
                    animation: isActive ? 'shimmer 1.4s ease-in-out infinite' : 'none',
                  }}>
                    ←
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Explanation panel */}
        {activeNode !== null && (() => {
          const exp = EXPLANATIONS[activeNode];
          return (
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(154,114,9,0.22)',
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              transition: 'all 0.3s ease',
            }}>
              <Typography sx={{
                fontFamily: 'Secular One, sans-serif', color: '#d4a017',
                fontSize: { xs: '1.1rem', md: '1.25rem' }, mb: 1.5,
              }}>
                {exp.title}
              </Typography>

              {exp.body.split('\n\n').map((para, pi) => (
                <Typography key={pi} sx={{
                  color: 'rgba(255,255,255,0.82)', fontFamily: 'Heebo, sans-serif',
                  fontSize: '1rem', lineHeight: 1.9,
                  mb: pi < exp.body.split('\n\n').length - 1 ? 1.5 : 2.5,
                }}>
                  {para}
                </Typography>
              ))}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box sx={{
                  bgcolor: 'rgba(154,114,9,0.12)', border: '1px solid rgba(154,114,9,0.2)',
                  borderRadius: 2, p: 2,
                }}>
                  <Typography sx={{
                    fontFamily: 'Secular One, sans-serif', color: '#9a7209',
                    fontSize: '0.72rem', letterSpacing: '0.1em',
                    textTransform: 'uppercase', mb: 0.75,
                  }}>
                    הערך
                  </Typography>
                  <Typography sx={{
                    color: 'rgba(255,255,255,0.78)', fontFamily: 'Heebo, sans-serif',
                    fontSize: '0.9rem', lineHeight: 1.7,
                  }}>
                    {exp.value}
                  </Typography>
                </Box>

                <Box sx={{
                  bgcolor: 'rgba(44,74,110,0.25)', border: '1px solid rgba(44,74,110,0.4)',
                  borderRadius: 2, p: 2,
                }}>
                  <Typography sx={{
                    fontFamily: 'Secular One, sans-serif', color: 'rgba(255,255,255,0.45)',
                    fontSize: '0.72rem', letterSpacing: '0.1em',
                    textTransform: 'uppercase', mb: 0.75,
                  }}>
                    לדוגמה
                  </Typography>
                  <Typography sx={{
                    color: 'rgba(255,255,255,0.68)', fontFamily: 'Heebo, sans-serif',
                    fontSize: '0.9rem', lineHeight: 1.7, fontStyle: 'italic',
                  }}>
                    {exp.example}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })()}


      </Box>
    </Box>
  );
};

export default ArchitectureSection;
